import { WEEKDAY_SHORT, draftFromEditor, listWords, type TimetableGroup } from "@teamna/shared";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { Fragment, type ReactNode } from "react";
import { Avatar, AvatarName, Button, EmptyState, ErrorState, LockIcon, Notice, PageHeader, SkeletonList } from "../components/index.ts";
import { useLabels } from "@teamna/web-client/hooks/use-labels.ts";
import { useActiveBusiness } from "@teamna/web-client/hooks/use-owner.ts";
import { useParticipants, useTeam, useTimetable } from "@teamna/web-client/hooks/use-setup.ts";
import { groupColor } from "../lib/group-color.ts";
import { useStaffPhotos } from "./staff-photos.ts";
import { timeLabel } from "./timetable/model.ts";

/**
 * One group: its facts, its weekly times, and who is in it.
 *
 * The weekly times are read only here. Every weekly time of every group is changed in one place, the
 * timetable (the Timetable view of `/owner/schedule`), which publishes them together and tells booked families once.
 */

export function OwnerGroupDetailScreen() {
  const { groupId } = useParams({ strict: false }) as { groupId?: string };
  const { business, isLoading, error } = useActiveBusiness();
  const { label } = useLabels();
  const businessId = business?.businessId ?? null;
  const timetable = useTimetable(businessId);
  const group = timetable.data?.groups.find((row) => row.id === groupId) ?? null;
  const Groups = label("group", { plural: true, capital: true });
  const back = { to: "/owner/groups", label: `Back to ${label("group", { plural: true })}` };

  if (!businessId || !group) {
    let body;
    if ((isLoading && !business) || timetable.isLoading) body = <SkeletonList rows={4} />;
    else if (error && !business) body = <ErrorState error={error} />;
    else if (timetable.error && !timetable.data) body = <ErrorState error={timetable.error} />;
    else if (!businessId) {
      body = (
        <EmptyState
          icon={<LockIcon size={30} />}
          title="No business to manage"
          message="This account does not run a business yet. Create one from the start page."
        />
      );
    } else {
      body = (
        <EmptyState
          title={`This ${label("group")} is not here`}
          message={`It may be deleted. Go back to the ${label("group", { plural: true })} to see the ones that run now.`}
        />
      );
    }
    return (
      <div>
        <PageHeader back={back} title={Groups} />
        {body}
      </div>
    );
  }

  return (
    <div>
      <GroupSummary key={group.id} businessId={businessId} group={group} back={back} />
    </div>
  );
}

function GroupSummary({
  businessId,
  group,
  back,
}: {
  businessId: string;
  group: TimetableGroup;
  back: { to: string; label: string };
}) {
  const { label } = useLabels();
  const navigate = useNavigate();
  const { may } = useActiveBusiness();
  const team = useTeam(businessId);
  const nameOf = (userId: string | null) =>
    (userId ? (team.data ?? []).find((member) => member.userId === userId)?.name : null) ?? `No ${label("staff")} yet`;
  const photoOf = useStaffPhotos(businessId);
  const { items } = draftFromEditor({ groups: [group], until: null, closures: [] });
  const isPrivate = group.kind === "private";

  // Each coach with the face next to the name.
  const coaches = group.staffUserIds.map((userId, index) => (
    <Fragment key={userId}>
      {index === 0 ? "" : index === group.staffUserIds.length - 1 ? " and " : ", "}
      <AvatarName name={group.staffNames[index] ?? nameOf(userId)} photoUrl={photoOf(userId)} />
    </Fragment>
  ));
  const facts: Array<[string, ReactNode]> = [
    ["kind", isPrivate ? "Private" : label("group", { capital: true })],
    ["length", `${group.durationMin} min`],
    ["places", isPrivate ? "1 place" : group.capacity === null ? "No limit" : `${group.capacity} places`],
    ["staff", group.staffUserIds.length ? (
      <>
        {label("staff", { plural: group.staffUserIds.length > 1, capital: true })}: {coaches}
      </>
    ) : (
      `No ${label("staff")} yet`
    )],
    ["placed", `${group.participantCount} ${group.participantCount === 1 ? label("participant") : label("participant", { plural: true })} placed`],
  ];

  return (
    <>
      <PageHeader
        back={back}
        eyebrow={label("group", { capital: true })}
        title={group.name}
        description={facts.map(([key, fact], index) => (
          <Fragment key={key}>
            {index === 0 ? "" : " · "}
            {fact}
          </Fragment>
        ))}
        actions={
          // The timetable is the owner's and the admins' (the front desk reads the class only).
          may("readTimetableEditor") ? (
            <Button variant="secondary" onClick={() => void navigate({ to: "/owner/schedule", search: { view: "timetable" } })}>
              Change in the schedule
            </Button>
          ) : null
        }
      />
      <section aria-labelledby="group-weekly" className="flex flex-col gap-3">
        <div className="flex items-center gap-2.5">
          <span aria-hidden="true" className="size-3 rounded-full" style={{ backgroundColor: groupColor(group.color, group.colorIndex) }} />
          <h2 id="group-weekly" className="text-[17px]">
            {isPrivate ? "Free hours for private sessions" : "Weekly sessions"}
          </h2>
        </div>
        {items.length === 0 ? (
          <p className="text-[14px] text-ink-2">
            No weekly time yet. Add one in the schedule: pick this {label("group")}, then click the week.
          </p>
        ) : (
          <ul className="divide-y divide-line rounded-card border border-line bg-paper">
            {items.map((item) => (
              <li key={item.key} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 px-4 py-3 text-[14px]">
                <span className="font-semibold tabular-nums">
                  {listWords([...item.days].sort((a, b) => a - b).map((day) => WEEKDAY_SHORT[day]!))},{" "}
                  {item.free ? `${timeLabel(item.start)} to ${timeLabel(item.end)}` : timeLabel(item.start)}
                </span>
                <span className="inline-flex items-center text-[13px] text-ink-2">
                  {item.staffUserId ? <AvatarName name={nameOf(item.staffUserId)} photoUrl={photoOf(item.staffUserId)} /> : nameOf(item.staffUserId)}
                  {item.free ? "" : `, ${item.end - item.start} min, ${item.capacity === null ? "no limit" : `${item.capacity} places`}`}
                </span>
              </li>
            ))}
          </ul>
        )}
        <Notice tone="info" title="Change weekly times in the schedule">
          The schedule holds every weekly time of every {label("group")}. You change them there, see what each change does to
          booked {label("participant", { plural: true })}, and publish them together.
        </Notice>
      </section>
      <GroupMembers businessId={businessId} group={group} />
    </>
  );
}

/**
 * Who is in this group: every participant placed in it, each a link to the profile of the member
 * who books for them. The count in the header says how many; this says who.
 */
function GroupMembers({ businessId, group }: { businessId: string; group: TimetableGroup }) {
  const { label } = useLabels();
  const participants = useParticipants(businessId);
  const placed = (participants.data ?? [])
    .filter((row) => row.groupId === group.id)
    .sort((a, b) => a.displayName.localeCompare(b.displayName));
  const Participants = label("participant", { plural: true, capital: true });

  return (
    <section aria-labelledby="group-members" className="mt-8 flex flex-col gap-3">
      <h2 id="group-members" className="text-[17px]">
        {Participants} in this {label("group")}
      </h2>
      {participants.isLoading && !participants.data ? (
        <SkeletonList rows={2} />
      ) : participants.error && !participants.data ? (
        <ErrorState error={participants.error} onRetry={() => void participants.refetch()} />
      ) : placed.length === 0 ? (
        <p className="text-[14px] text-ink-2">
          No {label("participant", { plural: true })} in this {label("group")} yet. Place them from {label("member", { plural: true })}.
        </p>
      ) : (
        <ul className="divide-y divide-line rounded-card border border-line bg-paper">
          {placed.map((row) => {
            const member = row.members[0] ?? null;
            return (
              <li key={row.id} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 px-4 py-3">
                <span className="flex min-w-0 items-center gap-3">
                  <Avatar name={row.displayName} photoUrl={row.photoUrl} size={32} />
                  <span className="min-w-0">
                    {member ? (
                      <Link
                        to="/owner/parents/$id"
                        params={{ id: member.userId }}
                        className="font-semibold text-accent-strong underline-offset-2 hover:underline"
                      >
                        <span dir="auto">{row.displayName}</span>
                      </Link>
                    ) : (
                      <span className="font-semibold text-ink" dir="auto">
                        {row.displayName}
                      </span>
                    )}
                    {member && member.name !== row.displayName ? (
                      <span className="block text-[13px] text-ink-2" dir="auto">
                        {member.name}
                      </span>
                    ) : null}
                  </span>
                </span>
                <span className="text-[13px] text-ink-2 tabular-nums">
                  {row.sessionsLeft} {row.sessionsLeft === 1 ? "session" : "sessions"} left
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
